if (!customElements.get('tabs-mobile-select')) {
  class TabsMobileSelect extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      this.selectWrapper = this.querySelector('.select');
      if (!this.select || !this.selectWrapper) return;

      this.buildCustomDropdown();

      this.onSelectChangeHandler = this.onSelectChange.bind(this);
      this.select.addEventListener('change', this.onSelectChangeHandler);

      this.onDocumentClickHandler = this.onDocumentClick.bind(this);
      document.addEventListener('click', this.onDocumentClickHandler);

      this.onKeydownHandler = this.onKeydown.bind(this);
      this.addEventListener('keydown', this.onKeydownHandler);
      this.list.addEventListener('keydown', this.onKeydownHandler);

      this.onScrollOrResizeHandler = this.close.bind(this);
      window.addEventListener('scroll', this.onScrollOrResizeHandler, { passive: true });
      window.addEventListener('resize', this.onScrollOrResizeHandler);
    }

    disconnectedCallback() {
      if (this.select) {
        this.select.removeEventListener('change', this.onSelectChangeHandler);
      }
      document.removeEventListener('click', this.onDocumentClickHandler);
      this.removeEventListener('keydown', this.onKeydownHandler);
      window.removeEventListener('scroll', this.onScrollOrResizeHandler);
      window.removeEventListener('resize', this.onScrollOrResizeHandler);
      if (this.list && this.list.parentNode) {
        this.list.parentNode.removeChild(this.list);
      }
    }

    buildCustomDropdown() {
      // The real <select> keeps working (and stays visible) if this script never runs.
      this.selectWrapper.classList.add('tabs__mobile-select--native-hidden');

      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'select__select tabs__mobile-select-trigger';
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.select.getAttribute('aria-label')) {
        this.trigger.setAttribute('aria-label', this.select.getAttribute('aria-label'));
      }

      this.triggerLabel = document.createElement('span');
      this.triggerLabel.className = 'tabs__mobile-select-trigger-label';
      this.trigger.appendChild(this.triggerLabel);

      const caret = this.querySelector('.icon-caret');
      if (caret) {
        this.trigger.appendChild(caret.cloneNode(true));
      }

      // Rendered into <body> (not left inside this element) so it can escape any
      // `isolation: isolate` / stacking-context ancestor and sit above every section.
      this.list = document.createElement('ul');
      this.list.className = 'tabs__mobile-select-list hidden';
      this.list.setAttribute('role', 'listbox');

      this.optionElements = Array.from(this.select.options).map((option, index) => {
        const item = document.createElement('li');
        item.className = 'tabs__mobile-select-option';
        item.setAttribute('role', 'option');
        item.setAttribute('tabindex', '-1');
        item.textContent = option.textContent;
        if (option.selected) {
          item.classList.add('tabs__mobile-select-option--active');
          item.setAttribute('aria-selected', 'true');
        }
        item.addEventListener('click', () => this.selectOption(index));
        this.list.appendChild(item);
        return item;
      });

      this.updateTriggerLabel();
      this.trigger.addEventListener('click', this.toggle.bind(this));

      this.insertBefore(this.trigger, this.selectWrapper);
      document.body.appendChild(this.list);
    }

    updateTriggerLabel() {
      const selected = this.select.options[this.select.selectedIndex];
      this.triggerLabel.textContent = selected ? selected.textContent : '';
    }

    updateActiveOption() {
      this.optionElements.forEach((item, index) => {
        const isActive = index === this.select.selectedIndex;
        item.classList.toggle('tabs__mobile-select-option--active', isActive);
        if (isActive) {
          item.setAttribute('aria-selected', 'true');
        } else {
          item.removeAttribute('aria-selected');
        }
      });
    }

    selectOption(index) {
      this.select.selectedIndex = index;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.close();
      this.trigger.focus();
    }

    onSelectChange() {
      this.updateTriggerLabel();
      this.updateActiveOption();

      const option = this.select.selectedOptions[0];
      if (!option) return;

      if (option.dataset.tabType === 'link') {
        window.location.href = option.value;
        return;
      }

      const radio = document.getElementById(`tab-item-input-${option.value}`);
      if (radio) {
        radio.checked = true;
      }
    }

    toggle() {
      if (this.list.classList.contains('hidden')) {
        this.open();
      } else {
        this.close();
      }
    }

    position() {
      const rect = this.trigger.getBoundingClientRect();
      this.list.style.top = `${rect.bottom}px`;
      this.list.style.left = `${rect.left}px`;
      this.list.style.width = `${rect.width}px`;
    }

    open() {
      this.position();
      this.list.classList.remove('hidden');
      this.trigger.setAttribute('aria-expanded', 'true');
      const active = this.optionElements[this.select.selectedIndex] || this.optionElements[0];
      if (active) active.focus();
    }

    close() {
      this.list.classList.add('hidden');
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    onDocumentClick(event) {
      if (!this.contains(event.target) && !this.list.contains(event.target)) {
        this.close();
      }
    }

    onKeydown(event) {
      const isOpen = !this.list.classList.contains('hidden');

      if (event.key === 'Escape') {
        if (isOpen) {
          this.close();
          this.trigger.focus();
        }
        return;
      }

      if (!isOpen) return;

      const currentIndex = this.optionElements.indexOf(document.activeElement);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const next = this.optionElements[currentIndex + 1] || this.optionElements[0];
        next.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const previous = this.optionElements[currentIndex - 1] || this.optionElements[this.optionElements.length - 1];
        previous.focus();
      } else if (event.key === 'Enter' || event.key === ' ') {
        if (currentIndex > -1) {
          event.preventDefault();
          this.selectOption(currentIndex);
        }
      }
    }
  }

  customElements.define('tabs-mobile-select', TabsMobileSelect);
}
